import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from './api'

/** One member connected to the open bid, as the breadcrumb bar needs them. */
export interface Connected {
  id: string
  name: string
  /** Their console agent has a task in flight (launched, not finished). */
  busy: boolean
  /** This is the signed-in member. */
  me: boolean
}

/**
 * The people connected to a bid — the ONE list behind both the "N connected" pill and the avatar
 * roster next to it (App.tsx, ConnectedRoster.tsx), so the two can never show different headcounts.
 * Bid-wide, whichever part is open: the per-part counts live in the left nav, not here.
 *
 * Same `['presence', bid]` query key (and 5s cadence) as the left nav's per-part counts, so every
 * reader shares one fetch. Off the bid views there is nothing to fetch and the list is empty.
 */
export function useConnected(bid: string | null): Connected[] {
  const me = useQuery({ queryKey: ['me'], queryFn: api.me })
  const presence = useQuery({
    queryKey: ['presence', bid],
    queryFn: () => api.presence(bid as string),
    enabled: Boolean(bid),
    refetchInterval: 5000,
  })

  return useMemo<Connected[]>(() => {
    const myId = me.data?.userId
    const rows: Connected[] = (presence.data?.users ?? []).map((u) => ({
      id: u.id,
      name: u.name,
      busy: u.agentBusy,
      me: u.id === myId,
    }))
    // The signed-in member is connected by definition, but the server only sees them once they
    // open a part (or their agent claims one) — so on the bid overview the list would otherwise be
    // empty, which reads as "nobody is here" rather than "just you". Own entry always first.
    const mine =
      rows.find((u) => u.me) ??
      (me.data ? { id: me.data.userId, name: me.data.name, busy: false, me: true } : null)
    const others = rows.filter((u) => !u.me)
    return mine ? [mine, ...others] : others
  }, [presence.data, me.data])
}
